import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from '../ui';
import { deltaTone } from '../../utils/categoryTrends';
import { chosenNamesTip } from '../../utils/branchCompare';

/** ▲ Uptrend / – Flat / ▼ Downtrend (any other value: neutral). */
export function TrendBadge({ trend }) {
    if (!trend) return null;
    const up = trend === 'Uptrend';
    const down = trend === 'Downtrend';
    const cls = up ? 'up' : down ? 'down' : 'flat';
    return (
        <span className={`br2-trend br2-trend--${cls}`}>
            <span aria-hidden="true">{up ? '▲' : down ? '▼' : '–'}</span>{trend}
        </span>
    );
}

TrendBadge.propTypes = { trend: PropTypes.string };

/** Signed whole percent; red below −5, green above +5 (today's Delta). */
export function Delta({ value }) {
    if (value == null) return <span className="br2-delta br2-delta--na">—</span>;
    const tone = deltaTone(value);
    return <span className={`br2-delta br2-delta--${tone}`}>{value > 0 ? '+' : ''}{Math.round(value)}%</span>;
}

Delta.propTypes = { value: PropTypes.number };

/**
 * A benchmark label ("vs 4 branches", "chosen branches"); with more chosen
 * branches than the label names, a focusable tooltip lists them (`plain`: a
 * hover title only, for labels repeated row after row). Otherwise the text.
 */
export function BenchLabel({ text, names = [], plain = false }) {
    const tip = chosenNamesTip(names);
    if (!tip) return text;
    if (plain) return <span title={tip}>{text}</span>;
    return (
        <Tooltip content={tip}>
            <span className="br2-figtip" tabIndex={0}>{text}</span>
        </Tooltip>
    );
}

BenchLabel.propTypes = { text: PropTypes.string.isRequired, names: PropTypes.arrayOf(PropTypes.string), plain: PropTypes.bool };

/** One KPI figure in a strip: label (with a focusable tip), value, sub-line. */
export function KpiTile({ label, tip, chip, value, valueClass, sub, testId }) {
    return (
        <div className="br2-ctk-i" data-testid={testId}>
            <span className="br2-ctk-l">
                {label}
                {chip && <span className="br2-mon">{chip}</span>}
                {tip && <Tooltip label={`About ${typeof label === 'string' ? label : 'this figure'}`} content={tip} />}
            </span>
            <span className={['br2-ctk-n', valueClass].filter(Boolean).join(' ')}>{value}</span>
            {sub && <span className="br2-ctk-p">{sub}</span>}
        </div>
    );
}

KpiTile.propTypes = {
    label: PropTypes.node.isRequired,
    tip: PropTypes.node,
    chip: PropTypes.node,
    value: PropTypes.node.isRequired,
    valueClass: PropTypes.string,
    sub: PropTypes.node,
    testId: PropTypes.string,
};
